import React, { useEffect, useState } from "react";

import { find } from "lodash/fp";

import { Collapse, Stack } from "@mui/material";

import Setting from "@app/analytics/account/components/Setting";
import {
  Divider,
  SettingSubtitle,
} from "@app/analytics/account/components/styles";
import ZoomOnlyChip from "@app/analytics/account/components/ZoomOnlyChip";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { isResponseErrorWithStatus } from "@app/common/utils";

import Button from "@rui/Button";

import AllowZoomAccessDialog from "./AllowZoomAccessDialog";
import ZoomLocalRecordingDisabledAlert from "./ZoomLocalRecordingDisabledAlert";

interface ReadAutoRecordingSelectorProps {
  // if this component should show the Allow Zoom Access Dialog if the auto record setting is not enabled
  showConfirmDialogIfNeeded: boolean;
}

const ReadAutoRecordingSelector = ({
  showConfirmDialogIfNeeded,
}: ReadAutoRecordingSelectorProps) => {
  const { userSettings, updateUserSettings } = useUserSettings();

  const isAutomaticRecordingEnabled = Boolean(
    userSettings.automaticRecordingEnabled
  );
  const [needsLocalRecording, setNeedsLocalRecording] = useState(false);

  const [showZoomDialog, setShowZoomDialog] = useState(
    showConfirmDialogIfNeeded
  );
  useEffect(() => {
    if (showConfirmDialogIfNeeded) {
      setShowZoomDialog(true);
    }
  }, [showConfirmDialogIfNeeded]);

  const { connectedAccountProviders, addAccountProvider, isLoading } =
    useConnectedProviders();

  const zoomProvider = find(
    { providerName: "zoom" },
    connectedAccountProviders
  );

  const zoomIsConnected = Boolean(zoomProvider);
  const reauthRequired = zoomProvider?.reauthenticationRequired === true;

  // if the user has a zoom account connected and need to re-auth,
  // don't disable the toggle so that we can send them into the re-auth flow
  // when they try to turn it on
  const hasZoomConnectedButNeedsReauth = zoomIsConnected && reauthRequired;

  const switchDisabled = isLoading;

  const handleToggleAutomaticRecordingEnabled = () => {
    setShowZoomDialog(false);
    if (!zoomIsConnected || hasZoomConnectedButNeedsReauth) {
      return addAccountProvider("zoom", {
        returnPath: ROUTES.USER_SETTINGS_RECORDING_AND_PLAYBACK,
      });
    }
    updateUserSettings.mutate(
      { automaticRecordingEnabled: true },
      {
        onError: async (error: any) => {
          if (!isResponseErrorWithStatus(error, 400)) {
            showToast({
              message:
                "There was an error, please try again or contact support@read.ai",
              severity: "error",
            });
            return;
          }

          const json = await error.response.json();
          const validationErrors = json.validation_errors;
          if (validationErrors.includes("zoom_local_recording_disabled")) {
            setNeedsLocalRecording(true);
          } else {
            showToast({
              message:
                "There was an error, please try again or contact support@read.ai",
              severity: "error",
            });
          }
        },

        onSuccess: () => {
          setNeedsLocalRecording(false);
          showToast({
            message: "Zoom Meeting Access successfully granted!",
            severity: "success",
          });
        },
      }
    );
  };

  const showSetting =
    !(isAutomaticRecordingEnabled || switchDisabled) || needsLocalRecording;

  return (
    <>
      {showSetting && (
        <AllowZoomAccessDialog
          open={showZoomDialog}
          onClose={() => setShowZoomDialog(false)}
          onConfirm={handleToggleAutomaticRecordingEnabled}
        />
      )}
      <Collapse in={showSetting}>
        <Stack spacing={1} sx={{ paddingTop: 1 }}>
          <Divider />
          <Setting
            title="Allow Automatic Zoom Meeting Access"
            endIcon={<ZoomOnlyChip />}
          >
            {needsLocalRecording && <ZoomLocalRecordingDisabledAlert />}

            {!isAutomaticRecordingEnabled && (
              <Stack direction="row" spacing={2} alignItems="center">
                <SettingSubtitle>
                  This setting must be enabled for Read to automatically access
                  Zoom video and audio data. Read will default to Read Lite
                  metrics if this is setting is disabled. However, you can still
                  grant Local Recording access during the meeting to use Read
                  with full capabilities.
                </SettingSubtitle>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleToggleAutomaticRecordingEnabled}
                >
                  Enable
                </Button>
              </Stack>
            )}
          </Setting>
        </Stack>
      </Collapse>
    </>
  );
};

export default ReadAutoRecordingSelector;
